import { useEffect, useState, useMemo, useCallback } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Chip,
  Divider,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Tab,
  Tabs
} from "@mui/material";
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Download as DownloadIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
  BarChart as BarChartIcon,
  PieChart as PieChartIcon,
  TrendingUp as TrendingUpIcon,
  Timeline as TimelineIcon,
  LocationOn as LocationOnIcon,
  LocalShipping as ExportIcon,
  AssignmentReturn as ImportIcon,
  Link as LinkageIcon,
  SwapHoriz as SubstitutionIcon,
  People as PeopleIcon,
  Business as BusinessIcon,
  Description as DescriptionIcon,
  AccountBalance as AccountBalanceIcon,
  Public as PublicIcon,
  CalendarToday as CalendarIcon,
  ShowChart as ShowChartIcon,
  Alert as AlertIcon
} from "@mui/icons-material";
import { format } from "date-fns";
import { GetDataFromApiWithParams } from "../../../../services/api/ExecuteApiRequests";

// Helper function to safely parse currency from string like "$200,000.00"
const parseCurrency = (value) => {
  if (!value && value !== 0) return 0;
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const cleanValue = value.replace(/[$,]/g, '').trim();
    const parsed = parseFloat(cleanValue);
    return isNaN(parsed) ? 0 : parsed;
  }
  return 0;
};

// Helper function to safely parse number from various formats
const safeParseNumber = (value) => {
  if (!value && value !== 0) return 0;
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const cleanValue = value.replace(/,/g, '').trim();
    const parsed = parseFloat(cleanValue);
    return isNaN(parsed) ? 0 : parsed;
  }
  return 0;
};

// Format currency
const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return "$0";
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

// Format number with commas
const formatNumber = (number) => {
  if (!number && number !== 0) return "0";
  return new Intl.NumberFormat('en-US').format(number);
};

// Tab Panel Component
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`trade-tabpanel-${index}`}
      aria-labelledby={`trade-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

// Bar Chart Component
const BarChart = ({ title, data, dataKey, color, formatValue }) => {
  const items = Object.keys(data);
  
  if (items.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="textSecondary">No data available</Typography>
      </Box>
    );
  }
  
  const maxValue = Math.max(...Object.values(data).map(d => d[dataKey] || 0), 0);
  
  if (maxValue === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="textSecondary">No data available for {title}</Typography>
      </Box>
    );
  }
  
  const barWidth = Math.min(50, Math.max(30, 80 - items.length * 2));
  
  return (
    <Box sx={{ p: 2, overflowX: 'auto' }}>
      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', color: '#1A237E', mb: 3, textAlign: 'center' }}>
        {title}
      </Typography>
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'flex-end', 
        gap: 2.5, 
        minHeight: 400, 
        justifyContent: 'center', 
        flexWrap: 'wrap',
        padding: 2
      }}>
        {items.map((item) => {
          const value = data[item][dataKey] || 0;
          const height = maxValue > 0 ? (value / maxValue) * 280 : 0;
          const percentage = maxValue > 0 ? ((value / maxValue) * 100).toFixed(1) : 0;
          
          return (
            <Tooltip 
              key={item} 
              title={`${item}: ${formatValue(value)} (${percentage}% of max)`} 
              arrow 
              placement="top"
            >
              <Box sx={{ 
                textAlign: 'center', 
                minWidth: 70,
                maxWidth: 120,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': {
                  transform: 'translateY(-5px)'
                }
              }}>
                <Box sx={{ 
                  position: 'relative',
                  height: height,
                  width: `${barWidth}px`,
                  bgcolor: color,
                  borderRadius: '4px 4px 0 0',
                  margin: '0 auto',
                  transition: 'all 0.3s ease',
                  opacity: 0.85,
                  '&:hover': {
                    opacity: 1,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
                  }
                }}>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      position: 'absolute', 
                      top: -22, 
                      left: '50%', 
                      transform: 'translateX(-50%)',
                      fontSize: '10px',
                      fontWeight: 'bold',
                      color: color,
                      whiteSpace: 'nowrap',
                      display: height > 35 ? 'none' : 'block'
                    }}
                  >
                    {formatValue(value)}
                  </Typography>
                </Box>
                
                {height > 35 && (
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      display: 'block',
                      fontSize: '10px',
                      fontWeight: 'bold',
                      color: 'white',
                      mt: -2.5,
                      mb: 0.5,
                      position: 'relative',
                      zIndex: 1,
                      textShadow: '0px 0px 2px rgba(0,0,0,0.5)'
                    }}
                  >
                    {formatValue(value)}
                  </Typography>
                )}
                
                <Typography 
                  variant="body2" 
                  sx={{ 
                    mt: 1.5, 
                    fontWeight: 'bold',
                    fontSize: '0.75rem',
                    maxWidth: '100px',
                    wordWrap: 'break-word',
                    lineHeight: 1.2,
                    color: '#333'
                  }}
                >
                  {item.length > 20 ? item.substring(0, 18) + '...' : item}
                </Typography>
                
                <Typography 
                  variant="caption" 
                  sx={{ 
                    display: 'block', 
                    color: '#888',
                    fontSize: '0.65rem',
                    mt: 0.25
                  }}
                >
                  ({percentage}%)
                </Typography>
              </Box>
            </Tooltip>
          );
        })}
      </Box>
      
      <Box sx={{ 
        width: '100%', 
        bgcolor: '#f8f9fa', 
        mt: 3, 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        p: 2, 
        borderRadius: 2,
        border: '1px solid #e0e0e0'
      }}>
        <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.75rem' }}>
          Total {title}: {formatValue(Object.values(data).reduce((sum, d) => sum + (d[dataKey] || 0), 0))}
        </Typography>
      </Box>
    </Box>
  );
};

// Pie Chart Component
const PieChart = ({ title, data, formatValue }) => {
  const items = Object.keys(data);
  const total = items.reduce((sum, key) => sum + (data[key] || 0), 0);
  const colors = ['#4caf50', '#ff9800', '#e91e63', '#2196f3', '#9c27b0', '#d32f2f', '#0288d1', '#7b1fa2', '#e65100', '#c2185b', '#00695c', '#f44336'];
  
  if (items.length === 0 || total === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="textSecondary">No data available for the selected filters</Typography>
      </Box>
    );
  }
  
  let startAngle = 0;
  const centerX = 120, centerY = 120, radius = 90;
  const svgSize = 280;
  
  const getPercentage = (value) => {
    return total > 0 ? ((value / total) * 100).toFixed(1) : 0;
  };
  
  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', color: '#1A237E', mb: 2, textAlign: 'center' }}>
        {title} - Total: {formatValue(total)}
      </Typography>
      
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, justifyContent: 'center', alignItems: 'center' }}>
        <Box sx={{ position: 'relative', width: svgSize, height: svgSize }}>
          <svg width={svgSize} height={svgSize} viewBox={`0 0 ${svgSize} ${svgSize}`}>
            {items.map((key, index) => {
              const value = data[key] || 0;
              const percentage = total > 0 ? (value / total) * 100 : 0;
              const angle = (percentage / 100) * 360;
              const endAngle = startAngle + angle;
              
              const startRad = (startAngle * Math.PI) / 180;
              const endRad = (endAngle * Math.PI) / 180;
              
              const x1 = centerX + radius * Math.cos(startRad);
              const y1 = centerY + radius * Math.sin(startRad);
              const x2 = centerX + radius * Math.cos(endRad);
              const y2 = centerY + radius * Math.sin(endRad);
              
              const largeArcFlag = angle > 180 ? 1 : 0;
              const pathData = `M ${centerX} ${centerY} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2} Z`;
              
              const slice = (
                <path 
                  key={key} 
                  d={pathData} 
                  fill={colors[index % colors.length]} 
                  stroke="white" 
                  strokeWidth="2"
                  style={{ cursor: 'pointer', transition: 'opacity 0.3s' }}
                  onMouseEnter={(e) => e.target.style.opacity = '0.8'}
                  onMouseLeave={(e) => e.target.style.opacity = '1'}
                />
              );
              startAngle = endAngle;
              return slice;
            })}
            
            <circle cx={centerX} cy={centerY} r="25" fill="white" />
            <text x={centerX} y={centerY - 5} textAnchor="middle" fontSize="11" fontWeight="bold" fill="#333">Total</text>
            <text x={centerX} y={centerY + 12} textAnchor="middle" fontSize="12" fontWeight="bold" fill="#1976d2">{formatValue(total)}</text>
          </svg>
        </Box>
        
        <Box sx={{ minWidth: 200, maxHeight: 250, overflowY: 'auto' }}>
          {items.map((key, index) => {
            const value = data[key] || 0;
            const percentage = getPercentage(value);
            return (
              <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Box sx={{ width: 12, height: 12, bgcolor: colors[index % colors.length], borderRadius: 1 }} />
                <Typography variant="caption" sx={{ flex: 1 }}>
                  {key.length > 20 ? key.substring(0, 18) + '...' : key} ({percentage}%)
                </Typography>
                <Typography variant="caption" fontWeight="bold" sx={{ color: colors[index % colors.length] }}>
                  {formatValue(value)}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>
      
      <Box sx={{ width: '100%', bgcolor: '#f5f5f5', mt: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', p: 2, borderRadius: 1 }}>
        <Typography variant="body2" color="textSecondary">Total Items: {items.length}</Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>Total Value: {formatValue(total)}</Typography>
      </Box>
    </Box>
  );
};

// Yearly Trend Line Chart
const YearlyTrendLineChart = ({ title, yearlyData, dataKeys, colors, formatValue }) => {
  const years = Object.keys(yearlyData).sort((a, b) => a - b);
  
  if (years.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="textSecondary">No yearly data available</Typography>
      </Box>
    );
  }
  
  const maxValue = Math.max(...Object.values(yearlyData).flatMap(d => 
    dataKeys.map(key => d[key] || 0)
  ), 0);
  
  const chartHeight = 350;
  const chartWidth = Math.max(600, years.length * 100);
  const padding = { top: 30, right: 40, bottom: 60, left: 80 };
  
  if (maxValue === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="textSecondary">No data available</Typography>
      </Box>
    );
  }
  
  return (
    <Box sx={{ p: 2, overflowX: 'auto' }}>
      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', color: '#1A237E', mb: 3, textAlign: 'center' }}>
        {title}
      </Typography>
      
      <svg width={chartWidth} height={chartHeight} viewBox={`0 0 ${chartWidth} ${chartHeight}`} preserveAspectRatio="xMidYMid meet">
        {[0, 25, 50, 75, 100].map((percent) => {
          const y = chartHeight - padding.bottom - (percent / 100) * (chartHeight - padding.top - padding.bottom);
          const value = (maxValue * percent) / 100;
          return (
            <g key={percent}>
              <line x1={padding.left} y1={y} x2={chartWidth - padding.right} y2={y} stroke="#e0e0e0" strokeWidth="1" strokeDasharray="4" />
              <text x={padding.left - 8} y={y} textAnchor="end" fontSize="10" fill="#666">{formatValue(value)}</text>
            </g>
          );
        })}
        
        {dataKeys.map((dataKey, idx) => (
          <polyline
            key={dataKey}
            points={years.map((year, index) => {
              const x = padding.left + (index / (years.length - 1 || 1)) * (chartWidth - padding.left - padding.right);
              const value = yearlyData[year]?.[dataKey] || 0;
              const y = chartHeight - padding.bottom - (value / maxValue) * (chartHeight - padding.top - padding.bottom);
              return `${x},${y}`;
            }).join(' ')}
            fill="none"
            stroke={colors[idx % colors.length]}
            strokeWidth="2.5"
          />
        ))}
        
        {years.map((year, index) => {
          const x = padding.left + (index / (years.length - 1 || 1)) * (chartWidth - padding.left - padding.right);
          
          return (
            <g key={year}>
              {dataKeys.map((dataKey, idx) => {
                const value = yearlyData[year]?.[dataKey] || 0;
                const y = chartHeight - padding.bottom - (value / maxValue) * (chartHeight - padding.top - padding.bottom);
                const color = colors[idx % colors.length];
                
                return (
                  <g key={dataKey}>
                    <circle cx={x} cy={y} r="5" fill={color} stroke="white" strokeWidth="2" style={{ cursor: 'pointer' }} />
                    {value > 0 && (
                      <text 
                        x={x} 
                        y={idx === 0 ? y - 10 : y + 15} 
                        textAnchor="middle" 
                        fontSize="8" 
                        fill={color}
                      >
                        {formatValue(value)}
                      </text>
                    )}
                  </g>
                );
              })}
              <text x={x} y={chartHeight - padding.bottom + 20} textAnchor="middle" fontSize="11" fontWeight="bold">{year}</text>
            </g>
          );
        })}
        
        <line x1={padding.left} y1={padding.top} x2={padding.left} y2={chartHeight - padding.bottom} stroke="#999" strokeWidth="1.5" />
        <line x1={padding.left} y1={chartHeight - padding.bottom} x2={chartWidth - padding.right} y2={chartHeight - padding.bottom} stroke="#999" strokeWidth="1.5" />
        
        <text x={20} y={chartHeight / 2} textAnchor="middle" fontSize="11" fill="#666" transform={`rotate(-90, 20, ${chartHeight / 2})`}>Amount (USD)</text>
        <text x={chartWidth / 2} y={chartHeight - 10} textAnchor="middle" fontSize="11" fill="#666">Year</text>
      </svg>
      
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, mt: 2, flexWrap: 'wrap' }}>
        {dataKeys.map((dataKey, idx) => {
          let label = "";
          if (dataKey === "export") label = "Export";
          else if (dataKey === "import") label = "Import";
          else if (dataKey === "importSubstitution") label = "Import Substitution";
          else if (dataKey === "linkage") label = "Linkage";
          else label = dataKey;
          
          return (
            <Box key={dataKey} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 20, height: 3, bgcolor: colors[idx % colors.length], borderRadius: 1 }} />
              <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>{label}</Typography>
            </Box>
          );
        })}
      </Box>
      
      <Box sx={{ width: '100%', bgcolor: '#f5f5f5', mt: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', p: 2, borderRadius: 1 }}>
        <Typography variant="body2" color="textSecondary">Years: {years.join(' → ')}</Typography>
      </Box>
    </Box>
  );
};

// Main Trade Activity Report Component
export default function TradeActivityReport() {
  // State Management
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Filter States
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedPark, setSelectedPark] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [startYear, setStartYear] = useState("all");
  const [endYear, setEndYear] = useState("all");
  const [startMonth, setStartMonth] = useState("all");
  const [endMonth, setEndMonth] = useState("all");
  
  // Chart States
  const [showChart, setShowChart] = useState(false);
  const [chartView, setChartView] = useState("park");
  const [parkChartTab, setParkChartTab] = useState(0);
  const [parkChartType, setParkChartType] = useState('bar');
  
  // Data States
  const [activitiesData, setActivitiesData] = useState([]);
  const [filteredActivities, setFilteredActivities] = useState([]);
  
  // Dynamic filter options
  const [parksList, setParksList] = useState(["all"]);
  const [statusesList, setStatusesList] = useState(["all"]);
  
  // Dialog States
  const [openDetailsDialog, setOpenDetailsDialog] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);

  // Generate last 10 years dynamically
  const getLastNYears = (n) => {
    const currentYear = new Date().getFullYear();
    const years = ["all"];
    for (let i = 0; i < n; i++) {
      years.push((currentYear - i).toString());
    }
    return years;
  };
  
  const years = getLastNYears(10);
  
  const months = [
    { value: "all", label: "All Months" },
    { value: "01", label: "January" }, { value: "02", label: "February" },
    { value: "03", label: "March" }, { value: "04", label: "April" },
    { value: "05", label: "May" }, { value: "06", label: "June" },
    { value: "07", label: "July" }, { value: "08", label: "August" },
    { value: "09", label: "September" }, { value: "10", label: "October" },
    { value: "11", label: "November" }, { value: "12", label: "December" }
  ];

  const getStatusColor = (status) => {
    const statusLower = status?.toLowerCase();
    switch(statusLower) {
      case 'active':
      case 'verified':
        return { bg: '#e8f5e9', color: '#2e7d32' };
      case 'inactive':
        return { bg: '#f5f5f5', color: '#9e9e9e' };
      case 'pending':
      case 'request':
        return { bg: '#fff3e0', color: '#ef6c00' };
      default: 
        return { bg: '#f5f5f5', color: '#616161' };
    }
  };

  // Park name mapping for park_id
  const parkMap = {
    "005": "Hawassa Special Economy Zone",
    "001": "Bole Lemi Industrial Park",
    "002": "Addis Ababa IPDC Park",
    "003": "Kilinto Industrial Park",
    "004": "Debre Birhan Industrial Park",
    "006": "Mekelle Industrial Park",
    "007": "Dire Dawa Industrial Park",
    "008": "ICT Park Addis",
  };

  // Fetch data from API
  // useEffect(() => {
  //   const fetchData = async () => {
  //     setLoading(true);
  //     try {
  //       const activityResponse = await GetDataFromApiWithParams(`investors/getemployment`);
        
  //       let activities = [];
        
  //       if (activityResponse && Array.isArray(activityResponse)) {
  //         activities = activityResponse;
  //       } else if (activityResponse?.data && Array.isArray(activityResponse.data)) {
  //         activities = activityResponse.data;
  //       }
        
  //       console.log("Raw API Data:", activities);
        
  //       const transformedActivities = activities.map(activity => {
  //         const exportAmount = parseCurrency(activity.export_amount);
  //         const importAmount = parseCurrency(activity.import);
  //         const linkageAmount = parseCurrency(activity.linkage_money);
  //         const importSubstitution = parseCurrency(activity.import_substitution);
          
  //         let year = "2024";
  //         let month = "01";
  //         if (activity.recorded_on) {
  //           const date = new Date(activity.recorded_on);
  //           year = date.getFullYear().toString();
  //           month = (date.getMonth() + 1).toString().padStart(2, '0');
  //         }
          
  //         let parkName = activity.park_name || "N/A";
  //         if ((!parkName || parkName === "N/A") && activity.park_id) {
  //           parkName = parkMap[activity.park_id] || `Park ${activity.park_id}`;
  //         }
          
  //         let status = activity.status || "REQUEST";
  //         if (status.toLowerCase() === "verified") {
  //           status = "VERIFIED";
  //         }
          
  //         return {
  //           id: activity.no || activity.id,
  //           investor_id: activity.investor_id,
  //           company_name: `Company ${activity.investor_id}`,
  //           park_name: parkName,
  //           year: year,
  //           month: month,
  //           export_amount: exportAmount,
  //           import_amount: importAmount,
  //           linkage_amount: linkageAmount,
  //           import_substitution: importSubstitution,
  //           status: status,
  //           description: activity.description || ""
  //         };
  //       });
        
  //       console.log("Transformed Activities:", transformedActivities);
        
  //       setActivitiesData(transformedActivities);
  //       setFilteredActivities(transformedActivities);
        
  //       const uniqueParks = [...new Set(transformedActivities.map(a => a.park_name).filter(name => name !== "N/A"))];
  //       setParksList(["all", ...uniqueParks]);
        
  //       const uniqueStatuses = [...new Set(transformedActivities.map(a => a.status))];
  //       setStatusesList(["all", ...uniqueStatuses]);
        
  //     } catch (error) {
  //       console.error("Failed to fetch trade data:", error);
  //     } finally {
  //       setLoading(false);
  //     }
  //   };
    
  //   fetchData();
  // }, []);
//***********************************************************new */
// Fetch data from API - Using the new trade data endpoint
useEffect(() => {
  const fetchData = async () => {
    setLoading(true);
    try {
      // Use the trade data endpoint
      const activityResponse = await GetDataFromApiWithParams(`investors/gettradedata`);
      
      let activities = [];
      
      if (activityResponse && Array.isArray(activityResponse)) {
        activities = activityResponse;
      } else if (activityResponse?.data && Array.isArray(activityResponse.data)) {
        activities = activityResponse.data;
      }
      
      console.log("Trade API Response:", activities);
      
      const transformedActivities = activities.map(activity => {
        const exportAmount = parseCurrency(activity.export_amount);
        const importAmount = parseCurrency(activity.import);
        const linkageAmount = parseCurrency(activity.linkage_money);
        const importSubstitution = parseCurrency(activity.import_substitution);
        
        let year = "2024";
        let month = "01";
        if (activity.recorded_on) {
          const date = new Date(activity.recorded_on);
          year = date.getFullYear().toString();
          month = (date.getMonth() + 1).toString().padStart(2, '0');
        } else if (activity.activity_period_to) {
          const date = new Date(activity.activity_period_to);
          year = date.getFullYear().toString();
          month = (date.getMonth() + 1).toString().padStart(2, '0');
        }
        
        let parkName = activity.park_name || "N/A";
        if ((!parkName || parkName === "N/A") && activity.park_id) {
          parkName = parkMap[activity.park_id] || `Park ${activity.park_id}`;
        }
        
        let status = activity.status || "REQUEST";
        if (status.toLowerCase() === "verified") {
          status = "VERIFIED";
        }
        
        return {
          id: activity.id || activity.no,
          investor_id: activity.investor_id,
          company_name: activity.company_name || `Company ${activity.investor_id}`,
          park_name: parkName,
          year: year,
          month: month,
          export_amount: exportAmount,
          import_amount: importAmount,
          linkage_amount: linkageAmount,
          import_substitution: importSubstitution,
          status: status,
          description: activity.description || ""
        };
      });
      
      console.log("Transformed Trade Activities:", transformedActivities);
      
      setActivitiesData(transformedActivities);
      setFilteredActivities(transformedActivities);
      
      const uniqueParks = [...new Set(transformedActivities.map(a => a.park_name).filter(name => name !== "N/A"))];
      setParksList(["all", ...uniqueParks]);
      
      const uniqueStatuses = [...new Set(transformedActivities.map(a => a.status))];
      setStatusesList(["all", ...uniqueStatuses]);
      
    } catch (error) {
      console.error("Failed to fetch trade data:", error);
    } finally {
      setLoading(false);
    }
  };
  
  fetchData();
}, []);
// Calculate summary statistics
  const summaryStats = useMemo(() => {
    const totalExport = filteredActivities.reduce((sum, a) => sum + (a.export_amount || 0), 0);
    const totalImport = filteredActivities.reduce((sum, a) => sum + (a.import_amount || 0), 0);
    const totalLinkage = filteredActivities.reduce((sum, a) => sum + (a.linkage_amount || 0), 0);
    const totalImportSubstitution = filteredActivities.reduce((sum, a) => sum + (a.import_substitution || 0), 0);
    const netTradeBalance = totalExport - totalImport;
    const uniqueInvestors = new Set(filteredActivities.map(a => a.investor_id)).size;
    const activeActivities = filteredActivities.filter(a => a.status?.toLowerCase() === 'active' || a.status?.toLowerCase() === 'verified').length;
    
    return {
      totalExport,
      totalImport,
      totalLinkage,
      totalImportSubstitution,
      netTradeBalance,
      uniqueInvestors,
      activeActivities,
      totalActivities: filteredActivities.length
    };
  }, [filteredActivities]);

  // Filter Data
  const applyFilters = useCallback(() => {
    let filteredList = [...activitiesData];
    
    if (searchTerm) {
      filteredList = filteredList.filter(item =>
        item.investor_id?.toString().toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.park_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.status?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (selectedPark !== "all") {
      filteredList = filteredList.filter(item => item.park_name === selectedPark);
    }
    
    if (selectedStatus !== "all") {
      filteredList = filteredList.filter(item => 
        item.status?.toLowerCase() === selectedStatus.toLowerCase()
      );
    }
    
    if (startYear !== "all" || endYear !== "all") {
      filteredList = filteredList.filter(item => {
        const itemYear = parseInt(item.year);
        const startYearNum = startYear !== "all" ? parseInt(startYear) : -Infinity;
        const endYearNum = endYear !== "all" ? parseInt(endYear) : Infinity;
        return itemYear >= startYearNum && itemYear <= endYearNum;
      });
    }
    
    if (startMonth !== "all" || endMonth !== "all") {
      filteredList = filteredList.filter(item => {
        const itemMonth = parseInt(item.month);
        const startMonthNum = startMonth !== "all" ? parseInt(startMonth) : 1;
        const endMonthNum = endMonth !== "all" ? parseInt(endMonth) : 12;
        return itemMonth >= startMonthNum && itemMonth <= endMonthNum;
      });
    }
    
    setFilteredActivities(filteredList);
    setPage(0);
  }, [activitiesData, searchTerm, selectedPark, selectedStatus, startYear, endYear, startMonth, endMonth]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  // Chart data preparation
  const chartData = useMemo(() => {
    const parkExportData = {};
    const parkImportData = {};
    const parkLinkageData = {};
    const parkImportSubstitutionData = {};
    const yearlyTradeData = {};
    
    filteredActivities.forEach(activity => {
      const park = activity.park_name || "Unassigned";
      const year = activity.year;
      
      const exportAmount = activity.export_amount || 0;
      const importAmount = activity.import_amount || 0;
      const linkageAmount = activity.linkage_amount || 0;
      const importSubstitution = activity.import_substitution || 0;
      
      parkExportData[park] = (parkExportData[park] || 0) + exportAmount;
      parkImportData[park] = (parkImportData[park] || 0) + importAmount;
      parkLinkageData[park] = (parkLinkageData[park] || 0) + linkageAmount;
      parkImportSubstitutionData[park] = (parkImportSubstitutionData[park] || 0) + importSubstitution;
      
      if (!yearlyTradeData[year]) {
        yearlyTradeData[year] = { export: 0, import: 0, linkage: 0, importSubstitution: 0 };
      }
      yearlyTradeData[year].export += exportAmount;
      yearlyTradeData[year].import += importAmount;
      yearlyTradeData[year].linkage += linkageAmount;
      yearlyTradeData[year].importSubstitution += importSubstitution;
    });
    
    return { parkExportData, parkImportData, parkLinkageData, parkImportSubstitutionData, yearlyTradeData };
  }, [filteredActivities]);

  // Render function for park charts
  const renderParkChart = (title, data, color, formatValue) => {
    const hasValidData = Object.values(data).some(value => value > 0);
    
    if (!hasValidData) {
      return (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="textSecondary">
            No data available for {title}
          </Typography>
        </Box>
      );
    }
    
    if (parkChartType === 'pie') {
      return <PieChart title={title} data={data} formatValue={formatValue} />;
    } else {
      const barData = {};
      Object.entries(data).forEach(([key, value]) => {
        barData[key] = { value: value };
      });
      return <BarChart title={title} data={barData} dataKey="value" color={color} formatValue={formatValue} />;
    }
  };

  // Export to CSV
  const handleExportCSV = () => {
    const headers = ['Investor ID', 'Park Name', 'Export Amount (USD)', 'Import Amount (USD)', 'Linkage Amount (USD)', 'Import Substitution (USD)', 'Net Trade Balance', 'Status', 'Year', 'Month'];
    const data = filteredActivities.map(item => [
      item.investor_id,
      item.park_name,
      item.export_amount || 0,
      item.import_amount || 0,
      item.linkage_amount || 0,
      item.import_substitution || 0,
      (item.export_amount || 0) - (item.import_amount || 0),
      item.status || 'Active',
      item.year,
      item.month
    ]);
    
    const csvContent = [headers, ...data].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `trade_activity_report_${format(new Date(), 'yyyyMMdd')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const resetFilters = () => {
    setSearchTerm("");
    setSelectedPark("all");
    setSelectedStatus("all");
    setStartYear("all");
    setEndYear("all");
    setStartMonth("all");
    setEndMonth("all");
  };

  const handleChangePage = (event, newPage) => { setPage(newPage); };
  const handleChangeRowsPerPage = (event) => { setRowsPerPage(parseInt(event.target.value, 10)); setPage(0); };
  const getCurrentPageData = () => { return filteredActivities.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage); };
  
  const handleViewDetails = (activity) => { 
    setSelectedActivity(activity); 
    setOpenDetailsDialog(true); 
  };

  const getTradeBalanceColor = (balance) => {
    if (balance > 0) return '#2e7d32';
    if (balance < 0) return '#c62828';
    return '#666';
  };

  return (
    <Box sx={{ width: '100%', bgcolor: '#f5f5f5', minHeight: '100vh', p: 3 }}>
      <Typography variant="h6" gutterBottom sx={{ color: '#1a237e', fontWeight: 'bold', textAlign: 'center', mb: 1 }}>
        Trade Activity Report
      </Typography>
      <Typography variant="body2" sx={{ textAlign: 'center', color: '#666', mb: 3 }}>
        Export, Import, Linkage & Import Substitution Analysis
      </Typography>
      
      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
        <Grid item xs={12} sm={6} md={2.4} sx={{ display: 'flex', justifyContent: 'center' }}>
          <Card sx={{ bgcolor: 'white', boxShadow: 1, width: '100%', maxWidth: '200px' }}>
            <CardContent sx={{ textAlign: 'center', py: 1, '&:last-child': { pb: 1 } }}>
              <ExportIcon sx={{ color: '#4caf50', fontSize: 20, mb: 0.5 }} />
              <Typography variant="h6" sx={{ color: '#1a237e', fontWeight: 'bold', fontSize: '1rem' }}>
                {formatCurrency(summaryStats.totalExport)}
              </Typography>
              <Typography variant="caption" sx={{ color: '#666', display: 'block', fontSize: '0.65rem' }}>Total Export</Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={2.4} sx={{ display: 'flex', justifyContent: 'center' }}>
          <Card sx={{ bgcolor: 'white', boxShadow: 1, width: '100%', maxWidth: '200px' }}>
            <CardContent sx={{ textAlign: 'center', py: 1, '&:last-child': { pb: 1 } }}>
              <ImportIcon sx={{ color: '#ff9800', fontSize: 20, mb: 0.5 }} />
              <Typography variant="h6" sx={{ color: '#1a237e', fontWeight: 'bold', fontSize: '1rem' }}>
                {formatCurrency(summaryStats.totalImport)}
              </Typography>
              <Typography variant="caption" sx={{ color: '#666', display: 'block', fontSize: '0.65rem' }}>Total Import</Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={2.4} sx={{ display: 'flex', justifyContent: 'center' }}>
          <Card sx={{ bgcolor: 'white', boxShadow: 1, width: '100%', maxWidth: '200px' }}>
            <CardContent sx={{ textAlign: 'center', py: 1, '&:last-child': { pb: 1 } }}>
              <LinkageIcon sx={{ color: '#2196f3', fontSize: 20, mb: 0.5 }} />
              <Typography variant="h6" sx={{ color: '#1a237e', fontWeight: 'bold', fontSize: '1rem' }}>
                {formatCurrency(summaryStats.totalLinkage)}
              </Typography>
              <Typography variant="caption" sx={{ color: '#666', display: 'block', fontSize: '0.65rem' }}>Total Linkage</Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={2.4} sx={{ display: 'flex', justifyContent: 'center' }}>
          <Card sx={{ bgcolor: 'white', boxShadow: 1, width: '100%', maxWidth: '200px' }}>
            <CardContent sx={{ textAlign: 'center', py: 1, '&:last-child': { pb: 1 } }}>
              <SubstitutionIcon sx={{ color: '#9c27b0', fontSize: 20, mb: 0.5 }} />
              <Typography variant="h6" sx={{ color: '#1a237e', fontWeight: 'bold', fontSize: '1rem' }}>
                {formatCurrency(summaryStats.totalImportSubstitution)}
              </Typography>
              <Typography variant="caption" sx={{ color: '#666', display: 'block', fontSize: '0.65rem' }}>Import Substitution</Typography>
            </CardContent>
          </Card>
        </Grid>
        
        
      </Grid>
      
      {/* Filters Section */}
      <Paper elevation={2} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (<InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>),
                endAdornment: searchTerm && (<InputAdornment position="end"><ClearIcon sx={{ cursor: 'pointer', fontSize: 'small' }} onClick={() => setSearchTerm("")} /></InputAdornment>)
              }}
            />
          </Grid>
          
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Park</InputLabel>
              <Select value={selectedPark} onChange={(e) => setSelectedPark(e.target.value)} label="Park">
                {parksList.map((park) => (<MenuItem key={park} value={park}>{park === "all" ? "All Parks" : park}</MenuItem>))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)} label="Status">
                {statusesList.map((status) => (<MenuItem key={status} value={status}>{status === "all" ? "All Status" : status}</MenuItem>))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={1.5}>
            <FormControl fullWidth size="small">
              <InputLabel>Year From</InputLabel>
              <Select value={startYear} onChange={(e) => setStartYear(e.target.value)} label="Year From">
                {years.map((year) => (<MenuItem key={year} value={year}>{year === "all" ? "All" : year}</MenuItem>))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={1.5}>
            <FormControl fullWidth size="small">
              <InputLabel>Year To</InputLabel>
              <Select value={endYear} onChange={(e) => setEndYear(e.target.value)} label="Year To">
                {years.map((year) => (<MenuItem key={year} value={year}>{year === "all" ? "All" : year}</MenuItem>))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={1.5}>
            <FormControl fullWidth size="small">
              <InputLabel>Month From</InputLabel>
              <Select value={startMonth} onChange={(e) => setStartMonth(e.target.value)} label="Month From">
                {months.map((month) => (<MenuItem key={month.value} value={month.value}>{month.label}</MenuItem>))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={1.5}>
            <FormControl fullWidth size="small">
              <InputLabel>Month To</InputLabel>
              <Select value={endMonth} onChange={(e) => setEndMonth(e.target.value)} label="Month To">
                {months.map((month) => (<MenuItem key={month.value} value={month.value}>{month.label}</MenuItem>))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
        
        <Grid container spacing={2} alignItems="center" justifyContent="flex-end">
          <Grid item>
            <Button variant="outlined" onClick={resetFilters} startIcon={<ClearIcon />} size="small" sx={{ textTransform: 'none' }}>
              Reset
            </Button>
          </Grid>
          <Grid item>
            <Button variant="contained" color="primary" size="small" onClick={handleExportCSV} startIcon={<DownloadIcon />} sx={{ textTransform: 'none' }}>
              Export CSV
            </Button>
          </Grid>
          <Grid item>
            <Button variant="outlined" size="small" onClick={() => setShowChart(!showChart)} sx={{ textTransform: 'none' }}>
              {showChart ? "Hide Chart" : "Show Chart"}
            </Button>
          </Grid>
        </Grid>
      </Paper>
      
      {/* Conditional Rendering: Show Chart OR Table */}
      {showChart ? (
        <>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center', gap: 2 }}>
            <Button variant={chartView === 'park' ? 'contained' : 'outlined'} size="small" onClick={() => setChartView('park')} sx={{ textTransform: 'none', minWidth: '120px' }}>
              <LocationOnIcon sx={{ mr: 1, fontSize: 18 }} />Park-wise View
            </Button>
            <Button variant={chartView === 'yearly' ? 'contained' : 'outlined'} size="small" onClick={() => setChartView('yearly')} sx={{ textTransform: 'none', minWidth: '120px' }}>
              <TimelineIcon sx={{ mr: 1, fontSize: 18 }} />Yearly Trend
            </Button>
          </Box>
          
          <Paper elevation={2} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
            {chartView === 'park' ? (
              <>
                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" color="textSecondary">Chart Type:</Typography>
                    <ToggleButtonGroup value={parkChartType} exclusive onChange={(e, val) => val && setParkChartType(val)} size="small">
                      <ToggleButton value="bar">
                        <Tooltip title="Bar Chart"><BarChartIcon fontSize="small" /></Tooltip>
                      </ToggleButton>
                      <ToggleButton value="pie">
                        <Tooltip title="Pie Chart"><PieChartIcon fontSize="small" /></Tooltip>
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </Box>
                </Box>
                
                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                  <Tabs value={parkChartTab} onChange={(e, newValue) => setParkChartTab(newValue)}>
                    <Tab label="Export by Park" />
                    <Tab label="Import by Park" />
                    <Tab label="Linkage by Park" />
                    <Tab label="Import Substitution by Park" />
                  </Tabs>
                </Box>
                
                <Box sx={{ mt: 2, minHeight: 450 }}>
                  <TabPanel value={parkChartTab} index={0}>
                    {renderParkChart("Export by Park", chartData.parkExportData, "#4caf50", (v) => formatCurrency(v))}
                  </TabPanel>
                  <TabPanel value={parkChartTab} index={1}>
                    {renderParkChart("Import by Park", chartData.parkImportData, "#ff9800", (v) => formatCurrency(v))}
                  </TabPanel>
                  <TabPanel value={parkChartTab} index={2}>
                    {renderParkChart("Linkage by Park", chartData.parkLinkageData, "#2196f3", (v) => formatCurrency(v))}
                  </TabPanel>
                  <TabPanel value={parkChartTab} index={3}>
                    {renderParkChart("Import Substitution by Park", chartData.parkImportSubstitutionData, "#9c27b0", (v) => formatCurrency(v))}
                  </TabPanel>
                </Box>
              </>
            ) : (
              <YearlyTrendLineChart 
                title="Yearly Trade Activity Trends" 
                yearlyData={chartData.yearlyTradeData} 
                dataKeys={['export', 'import', 'linkage', 'importSubstitution']}
                colors={['#4caf50', '#ff9800', '#2196f3', '#9c27b0']}
                formatValue={(v) => formatCurrency(v)}
              />
            )}
          </Paper>
        </>
      ) : (
        <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>Loading trade data...</Typography>
            </Box>
          ) : filteredActivities.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
              <Typography sx={{ color: 'text.secondary' }}>No trade activity data available</Typography>
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table size="small">
                  <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Investor ID</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Park Name</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', color: 'black' }}>Export (USD)</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', color: 'black' }}>Import (USD)</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', color: 'black' }}>Linkage (USD)</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', color: 'black' }}>Import Substitution</TableCell>
                      
                      
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>Year</TableCell>
                      
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {getCurrentPageData().map((row) => {
                      const netBalance = (row.export_amount || 0) - (row.import_amount || 0);
                      
                      return (
                        <TableRow key={row.id || row.investor_id} hover>
                          <TableCell>{row.investor_id}</TableCell>
                          <TableCell>{row.park_name}</TableCell>
                          <TableCell align="right" sx={{ color: 'black', fontWeight: 'medium' }}>{formatCurrency(row.export_amount)}</TableCell>
                          <TableCell align="right" sx={{ color: 'black' }}>{formatCurrency(row.import_amount)}</TableCell>
                          <TableCell align="right" sx={{ color: 'black' }}>{formatCurrency(row.linkage_amount)}</TableCell>
                          <TableCell align="right" sx={{ color: 'black' }}>{formatCurrency(row.import_substitution)}</TableCell>
                                            
                          <TableCell align="center">{row.year}</TableCell>
                          
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination 
                rowsPerPageOptions={[5, 10, 25, 50]} 
                component="div" 
                count={filteredActivities.length} 
                rowsPerPage={rowsPerPage} 
                page={page} 
                onPageChange={handleChangePage} 
                onRowsPerPageChange={handleChangeRowsPerPage} 
              />
            </>
          )}
        </Paper>
      )}
      
      {/* Details Dialog */}
      <Dialog open={openDetailsDialog} onClose={() => setOpenDetailsDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#1a237e' }}>
              Trade Activity Details
            </Typography>
            <IconButton onClick={() => setOpenDetailsDialog(false)}><CloseIcon /></IconButton>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent>
          {selectedActivity && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ color: '#666' }}>Investor ID</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'medium' }}>{selectedActivity.investor_id}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ color: '#666' }}>Park Name</Typography>
                  <Typography variant="body1">{selectedActivity.park_name}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ color: '#666' }}>Status</Typography>
                  <Chip label={selectedActivity.status || 'Active'} size="small" sx={getStatusColor(selectedActivity.status)} />
                </Grid>
                
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#1a237e', mb: 2 }}>Trade Breakdown (USD)</Typography>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: '#e8f5e9' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <ExportIcon sx={{ color: '#4caf50', fontSize: 20 }} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>Export</Typography>
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#2e7d32' }}>
                      {formatCurrency(selectedActivity.export_amount)}
                    </Typography>
                  </Paper>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fff3e0' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <ImportIcon sx={{ color: '#ff9800', fontSize: 20 }} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>Import</Typography>
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#ef6c00' }}>
                      {formatCurrency(selectedActivity.import_amount)}
                    </Typography>
                  </Paper>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: '#e3f2fd' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <LinkageIcon sx={{ color: '#2196f3', fontSize: 20 }} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>Linkage</Typography>
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#1565c0' }}>
                      {formatCurrency(selectedActivity.linkage_amount)}
                    </Typography>
                  </Paper>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f3e5f5' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <SubstitutionIcon sx={{ color: '#9c27b0', fontSize: 20 }} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>Import Substitution</Typography>
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#7b1fa2' }}>
                      {formatCurrency(selectedActivity.import_substitution)}
                    </Typography>
                  </Paper>
                </Grid>
                
                <Grid item xs={12}>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f5f5f5', mt: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>Net Trade Balance</Typography>
                    <Typography variant="h5" sx={{ 
                      fontWeight: 'bold', 
                      color: (selectedActivity.export_amount || 0) - (selectedActivity.import_amount || 0) >= 0 ? '#2e7d32' : '#c62828' 
                    }}>
                      {formatCurrency((selectedActivity.export_amount || 0) - (selectedActivity.import_amount || 0))}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button onClick={() => setOpenDetailsDialog(false)} variant="outlined">Close</Button>
        </Box>
      </Dialog>
    </Box>
  );
}